import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ImportResult, LearnedEntry, LearningConfigSummary, TargetType } from '../../services/learningApi';
import { useApis } from './common';

interface Props {
  targetType: TargetType;
  targetId: string;
  /** This target's learning config, left out of the source list. */
  configId: string;
  onClose: () => void;
  onImported: (message: string) => void;
}

const liveCount = (c: LearningConfigSummary) => (c.entry_counts?.active || 0) + (c.entry_counts?.pinned || 0);

const entrySummary = (e: LearnedEntry) => {
  if (e.kind === 'fact') return e.content.text || '';
  const worked = (e.content.items || []).filter((it) => it.positive - it.negative > 0).map((it) => it.label);
  const avoid = (e.content.items || []).filter((it) => it.positive - it.negative < 0).map((it) => it.label);
  return [worked.length ? `Worked: ${worked.slice(0, 3).join(', ')}` : '', avoid.length ? `Avoid: ${avoid.slice(0, 2).join(', ')}` : '']
    .filter(Boolean)
    .join(' · ');
};

export const ImportKnowledgeDialog: React.FC<Props> = ({ targetType, targetId, configId, onClose, onImported }) => {
  const apis = useApis();
  const [sources, setSources] = useState<LearningConfigSummary[] | null>(null);
  const [sourceId, setSourceId] = useState('');
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [mode, setMode] = useState<'all' | 'pick'>('all');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [list, setList] = useState<{ total: number; entries: LearnedEntry[] } | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [startAs, setStartAs] = useState<'candidate' | 'active'>('candidate');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const source = sources?.find((s) => s.id === sourceId) || null;
  const noun = targetType === 'chain' ? 'chain' : 'function';

  useEffect(() => {
    apis.learning
      .configs()
      .then((r) =>
        setSources(
          r.configs
            .filter((c) => c.id !== configId && liveCount(c) > 0)
            .sort((a, b) => (a.target_name || a.target_id).localeCompare(b.target_name || b.target_id)),
        ),
      )
      .catch((e) => {
        setError(e.message);
        setSources([]);
      });
  }, [apis, configId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPreview(null);
    setPicked([]);
    setList(null);
    if (!source) return;
    let cancelled = false;
    apis.learning
      .importKnowledge(targetType, targetId, { source_type: source.target_type, source_id: source.target_id, status: 'candidate', dry_run: true })
      .then((r) => !cancelled && setPreview(r))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [apis, targetType, targetId, source]);

  useEffect(() => {
    if (!source || mode !== 'pick') return;
    let cancelled = false;
    setListLoading(true);
    apis.learning
      .entries(source.target_type, source.target_id, { status: 'active,pinned', q: debouncedQ || undefined, sort: 'evidence', limit: 100 })
      .then((r) => !cancelled && setList(r))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setListLoading(false));
    return () => {
      cancelled = true;
    };
  }, [apis, source, mode, debouncedQ]);

  const alreadyHere = new Set(preview?.already_here_ids || []);
  const count = mode === 'all' ? preview?.to_import ?? 0 : picked.length;

  const submit = async () => {
    if (!source) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apis.learning.importKnowledge(targetType, targetId, {
        source_type: source.target_type,
        source_id: source.target_id,
        entry_ids: mode === 'pick' ? picked : null,
        status: startAs,
      });
      const skipped = r.already_here ? ` Skipped ${r.already_here} this ${noun} already has.` : '';
      onImported(
        `Imported ${r.imported} entr${r.imported === 1 ? 'y' : 'ies'} from “${r.source_name}”, ` +
          (r.status === 'active' ? 'in use now (published in a few seconds).' : 'waiting for your review.') +
          skipped,
      );
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import knowledge from another function or chain</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
            <li>
              <b>Copied:</b> later changes in either place don't affect the other.
            </li>
            <li>
              <b>Evidence starts over:</b> this {noun}'s own runs back each entry up (or contradict it) from now on.
            </li>
            <li>
              <b>Nothing here is overwritten:</b> names this {noun} already has, including rejected ones, are skipped.
            </li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Works best between {noun === 'chain' ? 'chains and functions' : 'functions'} that handle the same kind of input, like two that look up the same foods.
          </Typography>
        </Alert>

        {sources === null ? (
          <CircularProgress size={22} />
        ) : sources.length === 0 ? (
          <Alert severity="warning">No other function or chain in this account has knowledge in use yet.</Alert>
        ) : (
          <>
            <TextField
              select
              fullWidth
              size="small"
              label="Import from"
              value={sourceId}
              onChange={(e) => {
                setSourceId(e.target.value);
                setMode('all');
                setQ('');
              }}
              sx={{ mb: 2 }}
            >
              {sources.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.target_name || s.target_id}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {s.target_type} · {liveCount(s)} in use
                  </Typography>
                </MenuItem>
              ))}
            </TextField>

            {source && !preview && !error && <LinearProgress sx={{ mb: 2 }} />}
            {source && preview && (
              <>
                <Typography variant="body2" fontWeight={500}>
                  Which entries?
                </Typography>
                <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'all' | 'pick')} sx={{ mb: 1 }}>
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        Everything in use: {preview.to_import} new
                        {preview.already_here > 0 && ` (${preview.already_here} already here, skipped)`}
                        {preview.capped && ' — the 500 with the most evidence'}
                      </Typography>
                    }
                  />
                  <FormControlLabel value="pick" control={<Radio size="small" />} label={<Typography variant="body2">Let me choose</Typography>} />
                </RadioGroup>

                {mode === 'pick' && (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search names or topics"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                      sx={{ mb: 1 }}
                    />
                    {listLoading && <LinearProgress />}
                    <List dense sx={{ maxHeight: 280, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, py: 0 }}>
                      {list?.entries.length === 0 && (
                        <ListItem>
                          <ListItemText secondary="No in-use entries match." />
                        </ListItem>
                      )}
                      {list?.entries.map((e) => {
                        const here = alreadyHere.has(e.id);
                        const on = picked.includes(e.id);
                        return (
                          <ListItemButton
                            key={e.id}
                            dense
                            disabled={here}
                            onClick={() => setPicked((p) => (on ? p.filter((x) => x !== e.id) : [...p, e.id]))}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox size="small" edge="start" checked={on} disabled={here} tabIndex={-1} />
                            </ListItemIcon>
                            <ListItemText
                              primary={e.kind === 'fact' ? `Fact: ${e.key}` : e.key}
                              secondary={here ? 'Already here' : entrySummary(e) || undefined}
                              secondaryTypographyProps={{ noWrap: true }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                    {list && list.total > list.entries.length && (
                      <Typography variant="caption" color="text.secondary">
                        Showing {list.entries.length} of {list.total} with the most evidence. Search to find others.
                      </Typography>
                    )}
                  </Box>
                )}

                <Typography variant="body2" fontWeight={500}>
                  How should they start?
                </Typography>
                <RadioGroup value={startAs} onChange={(e) => setStartAs(e.target.value as 'candidate' | 'active')}>
                  <FormControlLabel
                    value="candidate"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        Waiting for review (recommended): approve them on this tab, or let this {noun}'s runs back them up
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="active"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">In use right away, like approving them</Typography>}
                  />
                </RadioGroup>
              </>
            )}
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!source || !preview || count === 0 || busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          Import {count > 0 ? count : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
